package input

import com.soywiz.korge.input.*
import com.soywiz.korge.ui.*
import com.soywiz.korge.view.*
import com.soywiz.korim.bitmap.*
import com.soywiz.korim.text.*
import com.soywiz.korma.geom.*
import ui.*

fun Container.addScreenControllerArrows(
    width: Double = 320.0,
    height: Double = 224.0,
    radius: Double = height / 8,
    leftArrowIcon: BmpSlice,
    leftArrowActiveIcon: BmpSlice,
    rightArrowIcon: BmpSlice,
    rightArrowActiveIcon: BmpSlice,
    jumpIcon: BmpSlice,
    jumpActiveIcon: BmpSlice,
    dashIcon: BmpSlice,
    dashActiveIcon: BmpSlice,
    dashDisabledIcon: BmpSlice
): InputControllerData {
    val controller = OnScreenControllerArrows()

    val leftArrow = leftArrowButton(width, height, radius, controller, leftArrowIcon, leftArrowActiveIcon)

    val rightArrow = rightArrowButton(width, height, radius, controller, rightArrowIcon, rightArrowActiveIcon)

    val jumpButton = jumpButton(width, height, radius, controller, jumpIcon, jumpActiveIcon)

    val firstAbilityButton = firstAbilityButton(
        width = width,
        height = height,
        radius = radius,
        icon = Bitmaps.transparent,
        activeIcon = Bitmaps.white,
        controller = controller
    )
    val secondAbilityButton = secondAbilityButton(
        width = width,
        height = height,
        radius = radius,
        icon = Bitmaps.transparent,
        activeIcon = Bitmaps.white,
        controller = controller
    )

    val dashButton = dashButton(width, height, radius, controller, dashIcon, dashActiveIcon, dashDisabledIcon)

    return InputControllerData(
        controller = controller,
        stick = null,
        jumpButton = jumpButton,
        firstAbilityButton = firstAbilityButton,
        secondAbilityButton = secondAbilityButton,
        dashButton = dashButton,
        leftArrow = leftArrow,
        rightArrow = rightArrow
    )
}

fun Container.customButton(
    buttonRadius: Double,
    icon: BmpSlice,
    activeIcon: BmpSlice,
    controller: ScreenController,
    buttonId: Int
) = container {
    val iconImage = image(texture = icon).apply {
        UIView.fitIconInRect(this, icon, buttonRadius, buttonRadius, Anchor.CENTER)
    }
    val activeImageIcon = image(texture = activeIcon).apply {
        UIView.fitIconInRect(this, activeIcon, buttonRadius, buttonRadius, Anchor.CENTER)
    }

    activeImageIcon.visible = false

    handleButton(
        buttonId,
        controller,
        onDown = {
            activeImageIcon.visible = true
        },
        onUp = {
            activeImageIcon.visible = false
        }
    )
}

fun Container.handleButton(
    buttonId: Int,
    controller: ScreenController,
    onDown: () -> Unit = {},
    onUp: () -> Unit = {}
) {
    val view = this
    var pressing = false
    var eventId: Int? = null
    var clicked = false

    touch {
        start {
            if (view.hitTest(it.global) != null &&
                !pressing &&
                eventId == null &&
                !clicked
            ) {
                pressing = true
                eventId = it.id
                onDown()
                controller.onButton(buttonId, pressing)
            }
        }

        end {
            if (it.id == eventId && !clicked) {
                eventId = null
                pressing = false
                onUp()
                controller.onButton(buttonId, pressing)
            }
        }
    }

    onDown {
        if (!pressing) {
            pressing = true
            clicked = true
            onDown()
            controller.onButton(buttonId, pressing)
        }
    }

    onUpAnywhere {
        if (clicked) {
            eventId = null
            pressing = false
            clicked = false
            onUp()
            controller.onButton(buttonId, pressing)
        }
    }
}

private fun Container.leftArrowButton(
    width: Double,
    height: Double,
    radius: Double,
    controller: ScreenController,
    icon: BmpSlice,
    activeIcon: BmpSlice,
): CustomButton {
    val arrowSize = radius * 1.5
    val button = CustomButton(
        label = "",
        width = arrowSize,
        height = arrowSize,
        icon = icon,
        activeIcon = activeIcon,
        buttonId = OnScreenControllerArrows.ARROW_LEFT
    ).apply {
        controllerSignal { signal ->
            controller.onButton(signal.buttonId, signal.pressing)
        }
        container.position(radius * 0.5, height - radius * 2)
    }

    button.container.addTo(this)

    return button
}

private fun Container.rightArrowButton(
    width: Double,
    height: Double,
    radius: Double,
    controller: ScreenController,
    icon: BmpSlice,
    activeIcon: BmpSlice,
): CustomButton {
    val arrowSize = radius * 1.5
    val button = CustomButton(
        label = "",
        width = arrowSize,
        height = arrowSize,
        icon = icon,
        activeIcon = activeIcon,
        buttonId = OnScreenControllerArrows.ARROW_RIGHT
    ).apply {
        controllerSignal { signal ->
            controller.onButton(signal.buttonId, signal.pressing)
        }
        container.position(radius * 0.5 + radius * 1.5 + radius * 0.5, height - radius * 2)
    }

    button.container.addTo(this)

    return button
}

private fun Container.jumpButton(
    width: Double,
    height: Double,
    radius: Double,
    controller: ScreenController,
    icon: BmpSlice,
    activeIcon: BmpSlice
): CustomButton {
    val jumpSize = radius * 1.5
    val button = CustomButton(
        label = "",
        width = jumpSize,
        height = jumpSize,
        icon = icon,
        activeIcon = activeIcon,
        buttonId = OnScreenControllerArrows.JUMP_BUTTON
    ).apply {
        controllerSignal { signal ->
            controller.onButton(signal.buttonId, signal.pressing)
        }

        container.position(width - radius * 2.0, height - radius * 2)
    }

    button.container.addTo(this)

    return button
}

fun Container.dashButton(
    width: Double,
    height: Double,
    radius: Double,
    controller: ScreenController,
    icon: BmpSlice,
    activeIcon: BmpSlice,
    disabledIcon: BmpSlice,
): CustomButton {
    val dashSize = radius * 1.5
    val dashButton = CustomButton(
        label = "",
        width = dashSize,
        height = dashSize,
        icon = icon,
        activeIcon = activeIcon,
        disabledIcon = disabledIcon,
        buttonId = OnScreenControllerArrows.DASH
    ).apply {
        controllerSignal { signal ->
            controller.onButton(signal.buttonId, signal.pressing)
        }
    }
    dashButton.container.addTo(this)
    dashButton.container.position(
        width - radius * 4.0,
        height - radius * 2
    )
    return dashButton
}

private fun Container.firstAbilityButton(
    width: Double,
    height: Double,
    radius: Double,
    icon: BmpSlice,
    activeIcon: BmpSlice,
    controller: ScreenController
): AbilityButton {
    val text = UIText("", width = radius, height = radius)

    val buttonContainer = customButton(radius, icon, activeIcon, controller, OnScreenControllerArrows.ABILITY_BUTTON_1)
        .position(width - radius * 1.6 - radius * 1.4 - radius * 0.5, height - radius * 1.6)
        .apply {
            text.addTo(this).apply {
                textAlignment = TextAlignment.CENTER
                visible = true
            }
        }
    return AbilityButton(
        buttonContainer, text
    )
}

private fun Container.secondAbilityButton(
    width: Double,
    height: Double,
    radius: Double,
    icon: BmpSlice,
    activeIcon: BmpSlice,
    controller: ScreenController
): AbilityButton {
    val text = UIText("", width = radius, height = radius)

    val buttonContainer = customButton(radius, icon, activeIcon, controller, OnScreenControllerArrows.ABILITY_BUTTON_2)
        .position(width - radius * 1.6, height - radius * 1.6 - radius * 1.4 - radius * 0.5)
        .apply {
            text.addTo(this).apply {
                textAlignment = TextAlignment.CENTER
                visible = true
            }
        }
    return AbilityButton(
        buttonContainer, text
    )
}

class OnScreenControllerArrows : ScreenController {

    override val left = KeyState()
    override val right = KeyState()
    override val jump = KeyState()
    override val down = KeyState()

    override val firstAbility = KeyState()
    override val secondAbility = KeyState()

    override val dash = KeyState()

    override fun onButton(buttonId: Int, pressed: Boolean) {
        when (buttonId) {
            ARROW_RIGHT -> {
                right.changeStateByPressed(pressed)
            }

            ARROW_LEFT -> {
                left.changeStateByPressed(pressed)
            }

            JUMP_BUTTON -> {
                jump.changeStateByPressed(pressed)
            }

            ABILITY_BUTTON_1 -> {
                firstAbility.changeStateByPressed(pressed)
            }

            ABILITY_BUTTON_2 -> {
                secondAbility.changeStateByPressed(pressed)
            }

            DASH -> {
                println("dash tapped")
                dash.changeStateByPressed(pressed)
            }

            else -> {
            }
        }
    }

    companion object {
        const val JUMP_BUTTON = 1
        const val ABILITY_BUTTON_1 = 2
        const val ABILITY_BUTTON_2 = 3
        const val ARROW_RIGHT = 4
        const val ARROW_LEFT = 5
        const val DASH = 6
    }
}
